import React, { useCallback, useEffect, useState } from 'react';
import {
	getSunshineHosts,
	pairSunshineHost,
	removeSunshineHost,
} from '../utils/sunshine';
import SunshineInstall from './SunshineInstall';

// Admin panel for managing Sunshine streaming connections: shows every devkit
// alongside its pairing state in the moonlight-web gateway, with pair/remove
// actions. Opened from the dashboard hamburger menu.

const StatusChip = ({ tone, children }) => {
	const tones = {
		green: 'bg-emerald-100 text-emerald-700',
		amber: 'bg-amber-100 text-amber-700',
		red: 'bg-red-100 text-red-700',
		gray: 'bg-gray-100 text-gray-600',
	};
	return (
		<span
			className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${tones[tone] || tones.gray}`}
		>
			{children}
		</span>
	);
};

export default function SunshineManager({ onClose }) {
	const [hosts, setHosts] = useState([]);
	const [devkits, setDevkits] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [busyIp, setBusyIp] = useState(null);
	const [rowMsg, setRowMsg] = useState({}); // ip -> error/info string
	const [installDevkit, setInstallDevkit] = useState(null);

	const refresh = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const [hostList, devkitRes] = await Promise.all([
				getSunshineHosts(),
				fetch('/api/get-devkits', { credentials: 'include' }).then((r) =>
					r.ok ? r.json() : { results: [] }
				),
			]);
			setHosts(hostList);
			setDevkits(
				(devkitRes.results || []).filter((d) => d?.tailscale?.ipv4?.[0])
			);
		} catch (e) {
			setError(e.message || 'Could not reach the streaming gateway');
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		refresh();
	}, [refresh]);

	// Prefer a paired entry if duplicates share the address
	const hostFor = (ip) => {
		const matches = hosts.filter((h) => h.address === ip);
		return matches.find((h) => h.paired === 'Paired') || matches[0] || null;
	};
	const orphanHosts = hosts.filter(
		(h) => !devkits.some((d) => d.tailscale.ipv4[0] === h.address)
	);

	const handlePair = async (ip) => {
		setBusyIp(ip);
		setRowMsg((m) => ({ ...m, [ip]: null }));
		try {
			await pairSunshineHost(ip);
		} catch (e) {
			setRowMsg((m) => ({ ...m, [ip]: e.message }));
		} finally {
			setBusyIp(null);
			refresh();
		}
	};

	const handleRemove = async (ip, hostId) => {
		setBusyIp(ip);
		setRowMsg((m) => ({ ...m, [ip]: null }));
		try {
			await removeSunshineHost(hostId);
		} catch (e) {
			setRowMsg((m) => ({ ...m, [ip]: e.message }));
		} finally {
			setBusyIp(null);
			refresh();
		}
	};

	const pairingState = (host, devkitOnline) => {
		if (!host) return <StatusChip tone='gray'>Not added</StatusChip>;
		if (host.paired !== 'Paired')
			return <StatusChip tone='amber'>Not paired</StatusChip>;
		if (host.server_state === null && !devkitOnline)
			return <StatusChip tone='red'>Paired · offline</StatusChip>;
		if (host.server_state === 'Busy')
			return <StatusChip tone='amber'>Paired · in use</StatusChip>;
		return <StatusChip tone='green'>Paired</StatusChip>;
	};

	const Row = ({ name, subtitle, ip, host, online, devkit }) => (
		<div className='py-3 px-4 border-b border-gray-100 last:border-b-0'>
			<div className='flex items-center justify-between gap-4'>
				<div className='min-w-0'>
					<div className='text-sm font-medium text-gray-900 truncate'>{name}</div>
					<div className='text-xs text-gray-500 font-mono'>{subtitle || ip}</div>
				</div>
				<div className='flex items-center gap-3 flex-shrink-0'>
					{pairingState(host, online)}
					{devkit && (
						<button
							onClick={() => setInstallDevkit(devkit)}
							disabled={!online}
							title={
								!online
									? 'Devkit is offline'
									: devkit.sunshine_installed
									? 'Reinstall / update Sunshine on this devkit'
									: 'Install Sunshine on this devkit'
							}
							className='px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
						>
							{devkit.sunshine_installed ? 'Reinstall' : 'Install'}
						</button>
					)}
					{(!host || host.paired !== 'Paired') && ip && (
						<button
							onClick={() => handlePair(ip)}
							disabled={busyIp === ip || !online}
							title={!online ? 'Devkit is offline' : undefined}
							className='px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5'
						>
							{busyIp === ip && (
								<svg className='animate-spin h-3 w-3' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={3}>
									<circle className='opacity-25' cx='12' cy='12' r='10' strokeLinecap='round' />
									<path className='opacity-75' d='M4 12a8 8 0 018-8' strokeLinecap='round' />
								</svg>
							)}
							{busyIp === ip ? 'Pairing…' : 'Pair'}
						</button>
					)}
					{host && (
						<button
							onClick={() => handleRemove(ip || host.address, host.host_id)}
							disabled={busyIp === (ip || host.address)}
							className='px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 hover:bg-red-50 text-gray-600 hover:text-red-600 border border-gray-200 hover:border-red-200 transition-colors disabled:opacity-50'
						>
							Remove
						</button>
					)}
				</div>
			</div>
			{busyIp === ip && (
				<p className='text-xs text-blue-600 mt-1.5'>
					Pairing runs automatically — no PIN entry needed. This can take up to
					a minute.
				</p>
			)}
			{rowMsg[ip] && busyIp !== ip && (
				<div className='mt-1.5 bg-red-50 border border-red-200 rounded-md px-2.5 py-1.5'>
					<p className='text-xs text-red-700 break-words'>{rowMsg[ip]}</p>
				</div>
			)}
		</div>
	);

	return (
		<div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
			<div className='bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-in fade-in zoom-in duration-200'>
				{/* Header */}
				<div className='bg-gradient-to-r from-gray-800 via-gray-900 to-black border-b border-gray-700 shadow-md text-white p-6 relative'>
					<button
						onClick={onClose}
						className='absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-2 transition-colors'
					>
						<svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
							<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
						</svg>
					</button>
					<div className='flex items-center gap-3'>
						<svg className='w-8 h-8 text-[#EBEBEB]' fill='none' stroke='currentColor' viewBox='0 0 24 24' strokeWidth={2}>
							<path strokeLinecap='round' strokeLinejoin='round' d='M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2zM10 7.5l4.5 2.5-4.5 2.5v-5zM9 21h6' />
						</svg>
						<h2 className='text-2xl font-bold'>Sunshine Streaming</h2>
					</div>
					<p className='mt-2 text-[#EBEBEB]'>
						Manage which devkits are paired for in-browser desktop streaming
					</p>
				</div>

				{/* Info strip */}
				<div className='bg-blue-50 border-b border-blue-100 px-4 py-2.5 flex items-start gap-2'>
					<svg className='w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24' strokeWidth={2}>
						<path strokeLinecap='round' strokeLinejoin='round' d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
					</svg>
					<p className='text-xs text-blue-800'>
						Pairing is fully automatic — the dashboard generates the PIN and
						submits it to the devkit's Sunshine for you. You never need to
						enter a code anywhere.
					</p>
				</div>

				{/* Content */}
				<div className='overflow-y-auto max-h-[calc(90vh-160px)]'>
					{loading ? (
						<div className='flex items-center justify-center py-16 gap-3 text-gray-500'>
							<svg className='animate-spin h-5 w-5' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
								<circle className='opacity-25' cx='12' cy='12' r='10' strokeLinecap='round' />
								<path className='opacity-75' d='M4 12a8 8 0 018-8' strokeLinecap='round' />
							</svg>
							<span className='text-sm'>Loading pairing status…</span>
						</div>
					) : error ? (
						<div className='p-6'>
							<div className='bg-red-50 border border-red-200 rounded-lg p-4'>
								<p className='text-sm text-red-700 break-words'>{error}</p>
								<button
									onClick={refresh}
									className='mt-3 px-4 py-2 text-sm font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors'
								>
									Retry
								</button>
							</div>
						</div>
					) : (
						<>
							<div className='px-4 pt-4 pb-1 flex items-center justify-between'>
								<h3 className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>
									DevKits
								</h3>
								<button
									onClick={refresh}
									className='text-xs text-blue-600 hover:text-blue-700 font-medium'
								>
									Refresh
								</button>
							</div>
							{devkits.length === 0 ? (
								<p className='px-4 py-6 text-sm text-gray-500'>No devkits found.</p>
							) : (
								devkits.map((d) => {
									const ip = d.tailscale.ipv4[0];
									return (
										<Row
											key={d.devkit_id}
											name={d.vessel_name || `DevKit ${d.devkit_id}`}
											subtitle={`${d.devkit_id} · ${ip}`}
											ip={ip}
											host={hostFor(ip)}
											online={d.status !== 'offline'}
											devkit={d}
										/>
									);
								})
							)}

							{orphanHosts.length > 0 && (
								<>
									<div className='px-4 pt-5 pb-1'>
										<h3 className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>
											Other gateway hosts
										</h3>
										<p className='text-xs text-gray-400 mt-0.5'>
											Paired in the gateway but not matching any devkit
										</p>
									</div>
									{orphanHosts.map((h) => (
										<Row
											key={h.host_id}
											name={h.name || h.address || `Host ${h.host_id}`}
											subtitle={h.address}
											ip={h.address}
											host={h}
											online={h.server_state !== null}
										/>
									))}
								</>
							)}
							<div className='h-3' />
						</>
					)}
				</div>
			</div>

			{installDevkit && (
				<SunshineInstall
					devkit={installDevkit}
					onClose={() => {
						setInstallDevkit(null);
						refresh();
					}}
				/>
			)}
		</div>
	);
}
